import { useCallback, useEffect, useState } from 'react'

export const BOOKING_URL = 'https://excelaestheticsmiami.janeapp.com'

// Distance (px) from the top of the viewport at which a heading counts as "reached".
const SPY_OFFSET = 140
// Space (px) left above a heading after clicking it in the index (fixed navbar is ~81px).
const SCROLL_OFFSET = 112

const slugify = (text) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)

// Headings pasted from docs often end with ": " or similar, which looks off in an index.
const cleanLabel = (text) =>
  text
    .replace(/\s+/g, ' ')
    .replace(/[\s:;,\-–—]+$/, '')
    .trim()

/**
 * Parses the blog HTML, makes sure every h2/h3 has a unique id and returns the
 * (possibly modified) HTML together with the heading structure:
 * [{ id, label, level, children: [{ id, label, level }] }]
 */
export const processBlogContent = (html) => {
  if (!html || typeof DOMParser === 'undefined') {
    return { html: html || '', sections: [] }
  }

  // Wrapping in a div keeps leading <style>/<meta> elements inside the body.
  const doc = new DOMParser().parseFromString(
    `<div id="__blog_root__">${html}</div>`,
    'text/html'
  )
  const root = doc.getElementById('__blog_root__')
  if (!root) return { html, sections: [] }

  const usedIds = new Set()
  root.querySelectorAll('[id]').forEach((el) => usedIds.add(el.id))

  const sections = []
  let currentSection = null

  root.querySelectorAll('h2, h3').forEach((el, index) => {
    const label = cleanLabel(el.textContent || '')
    if (!label) return

    let id = el.id
    if (!id) {
      const base = slugify(label) || `section-${index + 1}`
      id = base
      let suffix = 2
      while (usedIds.has(id)) {
        id = `${base}-${suffix}`
        suffix += 1
      }
      el.id = id
    }
    usedIds.add(id)

    const level = el.tagName === 'H2' ? 2 : 3
    const item = { id, label, level }

    if (level === 2) {
      currentSection = { ...item, children: [] }
      sections.push(currentSection)
    } else if (currentSection) {
      currentSection.children.push(item)
    } else {
      sections.push({ ...item, children: [] })
    }
  })

  return { html: root.innerHTML, sections }
}

export const scrollToHeading = (id) => {
  const el = document.getElementById(id)
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET
  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' })
}

// Runs `callback` once on mount and then on scroll/resize, at most once per frame.
const useFrameThrottledScroll = (callback, enabled = true) => {
  useEffect(() => {
    if (!enabled) return undefined

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(() => {
        ticking = false
        callback()
      })
    }

    callback()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [callback, enabled])
}

/** Returns the id of the last heading the reader has scrolled past (or null). */
export const useActiveHeading = (ids) => {
  const [activeId, setActiveId] = useState(null)

  const update = useCallback(() => {
    let current = null
    for (const id of ids) {
      const el = document.getElementById(id)
      if (!el) continue
      if (el.getBoundingClientRect().top - SPY_OFFSET <= 0) current = id
      else break
    }
    setActiveId(current)
  }, [ids])

  useFrameThrottledScroll(update, ids.length > 0)

  return ids.length > 0 ? activeId : null
}

/** Reading progress (0-100) of the element referenced by `ref`. */
export const useReadingProgress = (ref) => {
  const [progress, setProgress] = useState(0)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    if (rect.height <= 0) return
    const readingLine = window.innerHeight * 0.35
    const value = (readingLine - rect.top) / rect.height
    setProgress(Math.round(Math.min(1, Math.max(0, value)) * 100))
  }, [ref])

  useFrameThrottledScroll(update)

  return progress
}

/**
 * True while the reader is inside the article: after the article body starts
 * to enter the screen and until the closing call to action comes into view.
 */
export const useBookingBarVisible = (startRef, endRef) => {
  const [visible, setVisible] = useState(false)

  const update = useCallback(() => {
    const start = startRef.current
    const end = endRef.current
    const started = start ? start.getBoundingClientRect().top < window.innerHeight * 0.8 : false
    const finished = end ? end.getBoundingClientRect().top < window.innerHeight : false
    setVisible(started && !finished)
  }, [startRef, endRef])

  useFrameThrottledScroll(update)

  return visible
}
